import React, { useState } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Tag } from '@frontend/app/refresh-components';
import { CircleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { useToast } from '@/shadcn/hooks/use-toast';
import { useCustomStages } from '../context/CustomStagesContext';

interface Props {
  onSave: () => void;
  onCancel: () => void;
  editedName: string;
  statuses: Array<{ name: string; priority: number }>;
  tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: unknown[] }>;
  taskSettings?: {
    initialStatuses: Array<string>;
    finalStatus: string | null;
  };
}

export const CustomStagesSaveBar: React.FC<Props> = ({
  onSave,
  onCancel,
  editedName,
  statuses,
  tasks,
  taskSettings
}) => {
  // const { isNewStage, createNewStage } = useCustomStages();
  // const { handleSaveStage } = useCustomStages();
  const { handleSaveStage, hasUnsavedChanges, setHasUnsavedChanges } = useCustomStages();
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!editedName.trim()) {
      toast({
        title: 'Error',
        description: 'Stage name is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSaving(true);
      await handleSaveStage(editedName.trim(), statuses, tasks, taskSettings);

      toast({
        title: 'Success',
        description: 'Custom stage saved successfully',
      });

      onSave();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving stage:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save stage',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setHasUnsavedChanges(false);
    onCancel();
  };

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-1">
        <H3 className="mb-0 font-medium">Custom Stages</H3>
        {hasUnsavedChanges && (
          <Tag
            label={(
              <div className="flex items-center gap-1 text-yellow-5">
                <CircleExclamationIcon className="text-yellow-5" />
                <span>Unsaved Changes</span>
              </div>
            )}
            textColor="text-yellow-5"
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving || !hasUnsavedChanges}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || !editedName.trim()}
          loading={isSaving}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
